import { Link } from '@tanstack/react-router';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { SummaryCard } from '@nucleus-commerce/admin-kit/forms';
import type { Customization } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { FormField, FormItem, FormMessage } from '@nucleus-commerce/ui/components/form';
import { ScrollArea } from '@nucleus-commerce/ui/components/scroll-area';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useCustomizations, useSizeSets } from '@/features/dictionary';
import { BASE_CURRENCY } from '@/shared/catalog/base-currency';

import { useProductScope } from '../hooks/use-product-scope';
import type { ProductFormInput } from '../schemas/product-form-schema';

import { SharedChip } from './product-scope';

/** "+ $45.00"; a delta below zero already carries its sign. Empty when it adds nothing. */
function usePriceDelta() {
  const { t } = useTranslation('products');
  const format = useFormat();
  return (cents: number) => {
    if (cents === 0) return '';
    const price = format.money({ amount: cents, currency: BASE_CURRENCY });
    return cents > 0 ? t('rail.customizations.delta', { price }) : price;
  };
}

/** Which order-line inputs (ring size, engraving, a file) shoppers fill in for this product. */
export function CustomizationsSection() {
  const { t } = useTranslation(['products', 'dictionary']);
  const { readOnly } = useProductScope();
  const query = useCustomizations();
  const rows = [...(query.data ?? [])].sort((a, b) => a.position - b.position);

  return (
    <SummaryCard
      title={t('products:sections.customizations')}
      badge={<SharedChip />}
      description={readOnly ? undefined : t('products:rail.customizations.description')}
    >
      {query.isPending ? (
        <Skeleton className="h-5" />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} compact />
      ) : readOnly ? (
        <OfferedCustomizations rows={rows} />
      ) : (
        <CustomizationChecklist rows={rows} />
      )}
    </SummaryCard>
  );
}

/** For someone who may only read: what the product offers. */
function OfferedCustomizations({ rows }: { rows: readonly Customization[] }) {
  const { t } = useTranslation(['products', 'dictionary']);
  const form = useFormContext<ProductFormInput>();
  const selected = new Set(useWatch({ control: form.control, name: 'customizationIds' }));
  const priceDelta = usePriceDelta();
  const offered = rows.filter((row) => selected.has(row.id));

  if (offered.length === 0) {
    return (
      <span className="text-muted-foreground text-[13px]">
        {t('products:rail.customizations.none')}
      </span>
    );
  }
  return (
    <ul className="flex flex-col gap-1.5" aria-label={t('products:sections.customizations')}>
      {offered.map((row) => (
        <li key={row.id} className="flex items-baseline justify-between gap-2">
          <span className="min-w-0 truncate">{row.label}</span>
          <span className="text-muted-foreground shrink-0 text-xs tabular-nums">
            {[row.isRequired ? t('dictionary:required.yes') : '', priceDelta(row.priceDeltaCents)]
              .filter(Boolean)
              .join(' · ')}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** Choose which ones this product offers; what each one is stays in Catalog setup. */
function CustomizationChecklist({ rows }: { rows: readonly Customization[] }) {
  const { t } = useTranslation(['products', 'dictionary']);
  const form = useFormContext<ProductFormInput>();
  const details = useCustomizationDetails();
  // Before the first save nothing picked means the product type's defaults apply (see the mapper).
  const creating = useWatch({ control: form.control, name: 'basePrice' }) !== undefined;
  const productTypeId = useWatch({ control: form.control, name: 'productTypeId' });

  return (
    <>
      {rows.length === 0 ? (
        <p className="text-muted-foreground text-[13px]">{t('products:customizations.empty')}</p>
      ) : (
        <FormField
          control={form.control}
          name="customizationIds"
          render={({ field }) => {
            const selected = new Set(field.value);
            const toggle = (id: string, checked: boolean) => {
              const next = new Set(selected);
              if (checked) next.add(id);
              else next.delete(id);
              // Dictionary order, so the list reads the same everywhere.
              field.onChange(rows.filter((row) => next.has(row.id)).map((row) => row.id));
            };
            return (
              <FormItem>
                {creating && productTypeId && selected.size === 0 ? (
                  <p className="text-muted-foreground text-[13px]">
                    {t('products:optionsCard.customizationsFromType')}
                  </p>
                ) : null}
                <ScrollArea
                  type="auto"
                  className="-mx-1.5 [&_[data-slot=scroll-area-viewport]]:max-h-64 [&_[data-slot=scroll-area-viewport]>div]:!block"
                >
                  <ul aria-label={t('products:sections.customizations')}>
                    {rows.map((row) => (
                      <li key={row.id}>
                        <label className="hover:bg-muted/50 flex cursor-pointer items-start gap-2.5 rounded-md py-1.5 pr-3 pl-1.5">
                          <Checkbox
                            className="mt-0.5"
                            aria-label={t('products:rail.customizations.offer', {
                              label: row.label,
                            })}
                            checked={selected.has(row.id)}
                            onCheckedChange={(checked) => toggle(row.id, checked === true)}
                          />
                          <span className="flex min-w-0 flex-1 flex-col">
                            <span className="truncate text-[13px] font-medium">{row.label}</span>
                            <span className="text-muted-foreground text-xs">{details(row)}</span>
                          </span>
                          {row.isRequired ? (
                            <span className="bg-primary-tint text-primary-tint-foreground shrink-0 rounded-full px-1.5 text-[11px] font-semibold">
                              {t('dictionary:required.yes')}
                            </span>
                          ) : null}
                        </label>
                      </li>
                    ))}
                  </ul>
                </ScrollArea>
                <FormMessage />
              </FormItem>
            );
          }}
        />
      )}
      <Link to="/org/customizations" className="text-primary w-fit text-[13px] hover:underline">
        {t('products:rail.customizations.manage')}
      </Link>
    </>
  );
}

/** "Text · up to 20 characters · + $45.00": a customization's read-only details on one line. */
function useCustomizationDetails() {
  const { t } = useTranslation('dictionary');
  // Here rather than on the card: only the checklist names the size sets.
  const sizeSets = useSizeSets();
  const priceDelta = usePriceDelta();
  return (row: Customization) => {
    const sizeSet = row.sizeSetId
      ? sizeSets.data?.find((candidate) => candidate.id === row.sizeSetId)
      : undefined;
    return [
      t(`inputType.${row.inputType}`),
      sizeSet?.name ?? '',
      row.inputType === 'text' && row.maxLength !== null
        ? t('rules.maxLength', { count: row.maxLength })
        : '',
      priceDelta(row.priceDeltaCents),
    ]
      .filter(Boolean)
      .join(' · ');
  };
}
